import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";
import { api_v3_get_matches_by_tournament_with_date } from "../../utils/constants";

const MATCH_HIDDEN = "HIDDEN";
const MATCH_PENDING_DATE = "PENDING_DATE";
const MATCH_NOT_STARTED = "NOT_STARTED";
const MATCH_FINISHED = "FINISHED";
const MATCH_REVIEWED = "REVIEWED";

const DAYS_LABEL = ["Lun", "Mar", "Mie", "Jue", "Vie", "Sab", "Dom"];
const MONTHS_ABBR = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];

function fmtYMD(d) {
  const date = new Date(d);
  return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,"0")}-${String(date.getDate()).padStart(2,"0")}`;
}

function parseDate(str) {
  return new Date(str);
}

function isSameDay(a, b) {
  return fmtYMD(a) === fmtYMD(b);
}

const HomeCalendar = ({ startDate, endDate, periods, milestones, tournamentId, divisions }) => {
  const [, setLoading] = useState(true);
  const [, setError] = useState(false);
  const [matches, setMatches] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showLegend, setShowLegend] = useState(true);

  useEffect(() => {
    if (!tournamentId) return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(api_v3_get_matches_by_tournament_with_date(tournamentId));
        if (res.status === 200) {
          setMatches(res.data);
        }
        setLoading(false);
      } catch (err) {
        if (err.response && err.response.status === 404) {
          setMatches([]);
        } else {
          setError(true);
        }
        setLoading(false);
      }
    };
    load();
  }, [tournamentId]);

  const start = parseDate(startDate);
  const end = parseDate(endDate);

  if (!startDate || !endDate || !periods || !milestones) return null;
  if (!start || !end) return null;

  const getColorForDate = (date, hasMilestone, hasMatches) => {
    for (const period of periods) {
      const ps = parseDate(period.startDate);
      const pe = parseDate(period.endDate);
      if (date >= ps && date <= pe) {
        const color = period.color || "transparent";
        return hasMatches ? color : color + "44";
      }
    }
    return "transparent";
  };

  const getMilestonesForDate = (date) => {
    if (!date || !milestones) return [];
    return milestones.filter((m) => isSameDay(parseDate(m.date), date));
  };

  const getMatchesForDate = (date) => {
    if (!date || !matches) return [];
    return matches.filter((m) => m.matchDate && isSameDay(new Date(m.matchDate), date));
  };

  const today = new Date();

  const buildWeeks = () => {
    const oneDay = 24 * 60 * 60 * 1000;
    let startDow = start.getDay() - 1;
    startDow = startDow === -1 ? 6 : startDow;
    const adjustedStart = new Date(start.getTime() - startDow * oneDay);
    const weeks = [];
    for (let d = new Date(adjustedStart); d < end; d = new Date(d.getTime() + 7 * oneDay)) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        const day = new Date(d.getTime() + i * oneDay);
        week.push(day >= start && day <= end ? day : null);
      }
      weeks.push(week);
    }
    return weeks;
  };

  const weeks = buildWeeks();

  const handleDayClick = (day) => {
    setSelectedDay(day);
  };

  const filteredMatches = selectedDay ? getMatchesForDate(selectedDay) : [];
  const selectedMilestones = selectedDay ? getMilestonesForDate(selectedDay) : [];
  const selectedFormatted = selectedDay
    ? `${DAYS_LABEL[selectedDay.getDay() === 0 ? 6 : selectedDay.getDay() - 1]} ${String(selectedDay.getDate()).padStart(2,"0")} ${MONTHS_ABBR[selectedDay.getMonth()]} ${selectedDay.getFullYear()}`
    : "";

  return (
    <Wrapper className="v-anim-fade-up v-delay-5">
      <SectionLabel>CALENDARIO</SectionLabel>
      <SectionTitle>PARTIDOS</SectionTitle>
      <Inner>
        <CalendarPanel>
          <WeekGrid>
            {["L","M","X","J","V","S","D"].map((d) => (
              <DayHeader key={d}>{d}</DayHeader>
            ))}
          </WeekGrid>
          {weeks.map((week, wi) => (
            <WeekRow key={wi}>
              {week.map((day, di) => {
                if (!day) return <DayCell key={di} />;
                const hasMilestones = getMilestonesForDate(day).length > 0;
                const hasMatches = getMatchesForDate(day).length > 0;
                const bgColor = getColorForDate(day, hasMilestones, hasMatches);
                const isToday = isSameDay(day, today);
                const isSelected = selectedDay && isSameDay(day, selectedDay);
                return (
                  <DayCell key={di}>
                    <DayPill
                      style={{
                        backgroundColor: bgColor || "transparent",
                        ...(isToday ? { borderColor: "var(--v-gold)", boxShadow: "0 0 0 1px var(--v-gold)" } : {}),
                        ...(isSelected ? { borderColor: "var(--v-mint)", color: "var(--v-mint)" } : {}),
                      }}
                      onClick={() => handleDayClick(day)}
                      $hasContent={hasMatches || hasMilestones}
                    >
                      {day.getDate()}
                      {hasMilestones && <MilestoneDot />}
                    </DayPill>
                  </DayCell>
                );
              })}
            </WeekRow>
          ))}
          <LegendBox>
            <LegendToggle onClick={() => setShowLegend(!showLegend)}>
              {showLegend ? "▾" : "▸"} LEYENDA
            </LegendToggle>
            {showLegend && (
              <LegendItems>
                {periods.map((p, i) => (
                  p.name && (
                    <LegendItem key={i}>
                      <LegendSwatch style={{ backgroundColor: p.color }} />
                      {p.name}
                    </LegendItem>
                  )
                ))}
              </LegendItems>
            )}
          </LegendBox>
        </CalendarPanel>
        <DetailPanel>
          {selectedDay ? (
            <>
              <DetailDate>{selectedFormatted}</DetailDate>
              {selectedMilestones.length > 0 && (
                <MilestoneBlock>
                  <DetailSub>EVENTOS</DetailSub>
                  {selectedMilestones.map((m, i) => (
                    <MilestoneRow key={i}>
                      <MilestoneBullet />
                      {m.name}
                    </MilestoneRow>
                  ))}
                </MilestoneBlock>
              )}
              <DetailSub>PARTIDOS</DetailSub>
              {filteredMatches.length > 0 ? (
                <MatchList>
                  {filteredMatches.map((m) => (
                    <CalendarMatchCard key={m.matchId} match={m} />
                  ))}
                </MatchList>
              ) : (
                <EmptyState>No hay partidos este día.</EmptyState>
              )}
            </>
          ) : (
            <Placeholder>
              <PlaceholderIcon />
              <PlaceholderText>Selecciona un día para ver los partidos y eventos.</PlaceholderText>
            </Placeholder>
          )}
        </DetailPanel>
      </Inner>
    </Wrapper>
  );
};

const CalendarMatchCard = ({ match }) => {
  const isHidden = match.status === MATCH_HIDDEN;
  const isPendingDate = match.status === MATCH_PENDING_DATE;
  const notPlayed = match.status === MATCH_NOT_STARTED || isPendingDate;
  const isFinished = match.status === MATCH_FINISHED || match.status === MATCH_REVIEWED;

  const winnerA = isFinished &&
    ((match.playerAresult || 0) > (match.playerBresult || 0) || match.playerAaw === "AW");
  const winnerB = isFinished &&
    ((match.playerBresult || 0) > (match.playerAresult || 0) || match.playerBaw === "AW");

  const matchDateStr = match.matchDate && notPlayed
    ? new Date(match.matchDate).toLocaleString("es-ES", { timeZone: "Europe/Madrid" })
    : null;

  if (isHidden) return null;
  if (match.playerAusername === "DESCANSA" || match.playerBusername === "DESCANSA") return null;

  const divShort = match.divisionName === "Espadas Largas" ? "Esp. Largas"
    : match.divisionName === "Hombre de Armas" ? "H. de Armas"
    : match.divisionName;

  return (
    <MatchLink to={`/match/${match.matchId}/detail`}>
      <MatchCard>
        {matchDateStr && (
          <DateBadge>{matchDateStr}</DateBadge>
        )}
        <PlayerRow>
          <PlayerName className={winnerA ? "winner" : ""}>{match.playerAusername}</PlayerName>
          {!notPlayed && (
            <Result className={winnerA ? "winner" : ""}>
              {match.playerAaw ? match.playerAaw : ""} {match.playerAresult}
            </Result>
          )}
        </PlayerRow>
        <PlayerRow>
          <PlayerName className={winnerB ? "winner" : ""}>{match.playerBusername}</PlayerName>
          {!notPlayed && (
            <Result className={winnerB ? "winner" : ""}>
              {match.playerBaw ? match.playerBaw : ""} {match.playerBresult}
            </Result>
          )}
        </PlayerRow>
        {match.divisionName && (
          <DivTag>{divShort}</DivTag>
        )}
      </MatchCard>
    </MatchLink>
  );
};

const Wrapper = styled.section`
  width: 100%;
  opacity: 0;
`;

const SectionLabel = styled.p`
  font-family: var(--v-font-mono);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.8px;
  color: var(--v-mint);
  margin: 0 0 0.25rem 0;
`;

const SectionTitle = styled.h2`
  font-family: var(--v-font-ui);
  font-size: 34px;
  font-weight: 700;
  line-height: 1;
  color: var(--v-white);
  margin: 0 0 1.5rem 0;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const Inner = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const CalendarPanel = styled.div`
  flex: 0 0 auto;
  background: var(--v-surface);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  padding: 1.25rem;
  min-width: 320px;

  @media (max-width: 600px) {
    min-width: unset;
    width: 100%;
    padding: 0.75rem;
  }
`;

const WeekGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  margin-bottom: 0.5rem;
`;

const DayHeader = styled.div`
  text-align: center;
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.1px;
  color: var(--v-secondary);
  padding: 4px 0;
`;

const WeekRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
`;

const DayCell = styled.div`
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
`;

const DayPill = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--v-font-ui);
  font-size: 13px;
  font-weight: ${(p) => p.$hasContent ? "700" : "400"};
  color: var(--v-white);
  cursor: pointer;
  border: 1px solid transparent;
  transition: all var(--v-transition);
  position: relative;
  user-select: none;

  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
    background: rgba(255, 255, 255, 0.06);
  }

  @media (max-width: 600px) {
    width: 30px;
    height: 30px;
    border-radius: 15px;
    font-size: 11px;
  }
`;

const MilestoneDot = styled.div`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--v-gold-bright);
  position: absolute;
  top: 3px;
  right: 3px;

  @media (max-width: 600px) {
    width: 4px;
    height: 4px;
    top: 2px;
    right: 2px;
  }
`;

const LegendBox = styled.div`
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

const LegendToggle = styled.button`
  background: none;
  border: none;
  color: var(--v-secondary);
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.1px;
  cursor: pointer;
  padding: 0;
`;

const LegendItems = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const LegendItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-family: var(--v-font-mono);
  font-size: 9px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--v-secondary);
`;

const LegendSwatch = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 4px;
  display: inline-block;
`;

const DetailPanel = styled.div`
  flex: 1;
  background: var(--v-surface);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  padding: 1.5rem;
  min-height: 300px;

  @media (max-width: 900px) {
    width: 100%;
  }
`;

const DetailDate = styled.h3`
  font-family: var(--v-font-ui);
  font-size: 20px;
  font-weight: 700;
  color: var(--v-white);
  margin: 0 0 1rem 0;
`;

const DetailSub = styled.p`
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: var(--v-mint);
  margin: 1rem 0 0.5rem 0;
`;

const MilestoneBlock = styled.div`
  margin-bottom: 0.5rem;
`;

const MilestoneRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: var(--v-font-body);
  font-size: 14px;
  color: var(--v-white);
  margin-bottom: 0.25rem;
`;

const MilestoneBullet = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--v-gold-bright);
  flex-shrink: 0;
`;

const MatchList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 40vh;
  overflow-y: auto;
`;

const EmptyState = styled.p`
  font-family: var(--v-font-body);
  font-size: 14px;
  color: var(--v-secondary);
  font-style: italic;
`;

const Placeholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 200px;
  gap: 1rem;
`;

const PlaceholderIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const PlaceholderText = styled.p`
  font-family: var(--v-font-body);
  font-size: 14px;
  color: var(--v-secondary);
  text-align: center;
  max-width: 220px;
`;

const MatchLink = styled(Link)`
  text-decoration: none;
  color: inherit;

  &:hover {
    color: inherit;
  }
`;

const MatchCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 0.6rem 0.8rem;
  transition: border-color var(--v-transition), background var(--v-transition);

  &:hover {
    border-color: rgba(255, 255, 255, 0.15);
    background: rgba(255, 255, 255, 0.05);
  }
`;

const DivTag = styled.span`
  display: inline-block;
  margin-top: 0.35rem;
  padding: 1px 8px;
  font-family: var(--v-font-mono);
  font-size: 9px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--v-gold-bright);
  background: rgba(201, 160, 78, 0.1);
  border-radius: 10px;
`;

const DateBadge = styled.div`
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--v-mint);
  margin-bottom: 0.4rem;
  padding-bottom: 0.3rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
`;

const PlayerRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.15rem 0;
  gap: 0.5rem;
`;

const PlayerName = styled.span`
  font-family: var(--v-font-ui);
  font-size: 13px;
  font-weight: 500;
  color: var(--v-white);
  line-height: 1.4;

  &.winner {
    color: var(--v-mint);
    font-weight: 600;
  }
`;

const Result = styled.span`
  font-family: var(--v-font-mono);
  font-size: 12px;
  font-weight: 600;
  color: var(--v-secondary);
  flex-shrink: 0;

  &.winner {
    color: var(--v-mint);
  }
`;

export default HomeCalendar;
